/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-02-23 13:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import moment from 'moment';
import { SubmissionError } from 'redux-form';
import { store } from 'src/stores/store';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { SYNC_LIST } from 'src/constants/sync';
import { TIME_DURATION_OPTIONS } from 'src/constants/timer';
import { getStoreDate, getStoreTime, getUTCDate } from './date-time-helper';
import { generateTimeOptions } from 'src/helpers/time-helper';

/**
 * Build sync type options
 *
 * @param  {Object} availableSyncList list sync types
 *
 * @return {Object} initialValue
 */
export const buildSyncTypeOptions = (availableSyncList: Array<string>, i18n: Object) => {
  if (_.isEmpty(availableSyncList)) {
    return [];
  }

  return _.map(availableSyncList, item => {
    return {
      value: item,
      label: SYNC_LIST[item] ? i18n.translate(SYNC_LIST[item]) : SYNC_LIST[item]
    };
  });
};

/**
 * Build initial data for sync schedule
 *
 * @param  {Object} data form data
 *
 * @return {Object} value for API
 */
export const buildInitialSyncSchedule = (data: Object = {}, currentTime: Date, tenantId = '') => {
  const duration = TIME_DURATION_OPTIONS[tenantId] || TIME_DURATION_OPTIONS['default'];
  const TIME_OPTIONS = generateTimeOptions(duration);
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDateNumber = DATE_FORMAT[appLanguage].number;

  if (_.isEmpty(data)) {
    data = {};
  }

  let { flowName = '', scheduleAt = currentTime, scheduleId = '' } = data;
  const selectedDate = moment(getStoreDate(scheduleAt)).format(formatDateNumber);
  const { hour, minute } = getStoreTime(scheduleAt);

  let value = hour * 60 + minute;
  const index = _.findIndex(TIME_OPTIONS, item => item.value >= value);

  const selectedTime = index === -1 ? TIME_OPTIONS[0] : TIME_OPTIONS[index];

  return { flowName, selectedDate, selectedTime, scheduleId };
};

/**
 * Validate for sync schedule
 *
 * @param  {object} data sync schedule data
 * @param  {I18n} i18n
 *
 * @return {boolean/error} void if well validated, throw error if have
 */
export const validateSyncSchedule = (data: Object, i18n: Object) => {
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDateNumber = DATE_FORMAT[appLanguage].number;

  // Check time value
  const { flowName, selectedDate, selectedTime } = data;
  if (!_.isEmpty(flowName)) {
    // Has sync data
    // Check time
    const dateDiff = moment(selectedDate, formatDateNumber).diff(getStoreDate(new Date()), 'days');
    if (dateDiff === 0) {
      const { hour, minute } = getStoreTime(new Date());
      if (selectedTime.value < hour * 60 + minute)
        throw new SubmissionError({
          selectedTime: i18n.translate('AdminHTML.Sync.SyncSchedule.invalidTime')
        });
    }
  }
  return true;
};

/**
 * Build data for sync schedule
 *
 * @param  {Object} data form data
 *
 * @return {Object} value for API
 */
export const buildSyncSchedule = (data: Object) => {
  const { flowName, selectedDate, selectedTime, scheduleId } = data;
  const { value: time = 0 } = selectedTime;
  const hour = Math.floor(time / 60);
  const minute = time % 60;

  const scheduleAt = getUTCDate(new Date(`${selectedDate} ${hour}:${minute}`), hour, minute).unix();
  return { flowName, scheduleAt, scheduleId };
};
